import { Fragment, useEffect, useState, useMemo, useCallback } from "react";
import React from "react";
import DataTable from "../../../../components/ui/table/DataTable";
import FilterModal from "../../../../components/ui/popup/FilterModal";
import EmptyState from "../../../../components/ui/pages/EmptyState";
import GhostCardContainer from "../../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostDataTable from "../../../../components/ui/loading/skeletons/GhostDataTable";
import LoadingToRealTransitionWrapper from "../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import { useRefreshOnAuth } from "../../../../hooks/useRefreshOnAuth";
import {
  fetchNextPayout,
  fetchPayoutHistory,
} from "../../../../services/payments/paymentPayout";
import { getLanguageContent } from "../../../../utils/language/languageSelector";
import { FILTER_SCHEMA } from "../../../../utils/schemas/language/components/filterSchema";

const Payouts = () => {
  const filterContent = getLanguageContent(FILTER_SCHEMA);
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [filteredPayouts, setFilteredPayouts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [lastPayoutId, setLastPayoutId] = useState(null);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // FIX: Added missing state hooks for summary values
  const [nextPayout, setNextPayout] = useState(null);
  const [payoutsAvailable, setPayoutsAvailable] = useState(0);

  // Active filter rule states
  const [filters, setFilters] = useState({
    date: "",
    status: "",
  });

  // Intent-locked filter modifications state window
  const [draftFilters, setDraftFilters] = useState({
    date: "",
    status: "",
  });

  const headers = [
    {
      label: "Created On",
      key: "created",
      sortable: true,
      type: "tag",
      width: "200px",
    },
    {
      label: "Est. Arrival",
      key: "arrivalDate",
      sortable: true,
      width: "200px",
    },
    { label: "Amount", key: "amount", sortable: true, width: "200px" },
    {
      label: "Status",
      key: "status",
      sortable: true,
      type: "filing_status",
      width: "200px",
    },
  ];

  // ============================================================
  // DATA INTERFACE PARSING & TRANSFORM LOGIC
  // ============================================================
  const transformPayout = useCallback((payout) => {
    console.log(payout);
    let formattedCreated = "";
    if (payout.created) {
      const date =
        typeof payout.created === "number"
          ? new Date(payout.created * 1000)
          : new Date(payout.created);

      if (!isNaN(date.getTime())) {
        formattedCreated = `${String(date.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(date.getDate()).padStart(2, "0")}-${date.getFullYear()}`;
      }
    }

    let formattedArrivalDate = "";
    if (payout.arrival_date) {
      const date =
        typeof payout.arrival_date === "number"
          ? new Date(payout.arrival_date * 1000)
          : new Date(payout.arrival_date);

      if (!isNaN(date.getTime())) {
        formattedArrivalDate = `${String(date.getMonth() + 1).padStart(
          2,
          "0",
        )}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
      }
    }

    const amountInDollars =
      typeof payout.amount === "number"
        ? payout.amount
        : parseFloat(payout.amount) || 0;

    const rawStatus = payout.status ? payout.status.toLowerCase() : "";
    let formattedStatus = "Draft";

    if (rawStatus === "paid" || rawStatus === "in_transit") {
      formattedStatus = "Completed";
    } else if (rawStatus === "pending") {
      formattedStatus = "Active";
    } else if (rawStatus === "failed" || rawStatus === "canceled") {
      formattedStatus = "Cancelled";
    }

    return {
      id: payout.id,
      created: formattedCreated,
      arrivalDate: formattedArrivalDate,
      amount: `$${amountInDollars.toFixed(2)}`,
      status: formattedStatus,
      details: payout.details || [],

      created_value:
        payout.created && typeof payout.created === "number"
          ? payout.created
          : new Date(payout.created).getTime() / 1000,
      arrivalDate_value:
        payout.arrival_date && typeof payout.arrival_date === "number"
          ? payout.arrival_date
          : new Date(payout.arrival_date).getTime() / 1000,
      amount_value: amountInDollars,
    };
  }, []);

  // ============================================================
  // BACKEND API LIFECYCLE MANAGEMENT
  // ============================================================
  const loadNextPayoutData = useCallback(async () => {
    try {
      const response = await fetchNextPayout();
      if (!response.ok) throw new Error("Failed to fetch next payout");
      const data = await response.json();
      setNextPayout(data.next_payout);
      setPayoutsAvailable(data.available || 0);
    } catch (err) {
      console.error("Error fetching next payout:", err);
      setNextPayout(null);
      setPayoutsAvailable(0);
    }
  }, []);

  const loadPayoutHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const startingAfter = currentPage === 1 ? null : lastPayoutId;
      const response = await fetchPayoutHistory(startingAfter);

      if (response.status === 401) return;

      const data = await response.json();

      if (response.status === 200) {
        const transformedPayouts = data.payouts.map(transformPayout);
        setPayouts(transformedPayouts);

        if (transformedPayouts.length > 0) {
          setLastPayoutId(transformedPayouts[transformedPayouts.length - 1].id);
        }
        setHasMore(data.has_more || data.hasMore || false);
      } else {
        const errorMessage =
          response.status === 502
            ? "Failed to fetch payouts from Stripe"
            : "Failed to fetch payout history";
        setError(data.message || data.error || errorMessage);
        setPayouts([]);
      }
    } catch (err) {
      console.error("Error fetching payout history:", err);
      setError("Failed to fetch payout history. Please try again later.");
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, lastPayoutId, transformPayout]);

  const handleRefresh = useCallback(() => {
    loadNextPayoutData();
    loadPayoutHistory();
  }, [loadNextPayoutData, loadPayoutHistory]);

  useRefreshOnAuth(handleRefresh);

  useEffect(() => {
    loadPayoutHistory();
  }, [loadPayoutHistory]);

  useEffect(() => {
    loadNextPayoutData();
  }, [loadNextPayoutData]);

  useEffect(() => {
    let filtered = payouts;

    if (filters.date) {
      const [year, month, day] = filters.date.split("-");
      const formattedDate = `${month}/${day}/${year}`;
      filtered = filtered.filter(
        (payout) => payout.arrivalDate === formattedDate,
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (payout) =>
          payout.status.toLowerCase() === filters.status.toLowerCase(),
      );
    }

    setFilteredPayouts(filtered);
  }, [payouts, filters]);

  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some(
      (value) => value !== "",
    );
    if (hasActiveFilters && currentPage !== 1) {
      setCurrentPage(1);
      setLastPayoutId(null);
    }
  }, [filters, currentPage]);

  const totalPagesCalculation = useMemo(() => {
    return hasMore ? currentPage + 1 : currentPage;
  }, [hasMore, currentPage]);

  return (
    <Fragment>
      <div className="internal-content-body">
        {error ? (
          <EmptyState title="Unable to load payouts" text={error} />
        ) : (
          <Fragment>
            <div className="payout-container" style={{ marginBottom: "24px" }}>
              <div className="payout-row">
                <div className="payout-item">
                  <span>
                    <strong>Next Payout:</strong>
                  </span>
                  <span>{nextPayout || "—"}</span>
                </div>

                <div className="payout-item">
                  <span>
                    <strong>Available:</strong>
                  </span>
                  <span>${(Number(payoutsAvailable) || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <LoadingToRealTransitionWrapper
              isLoading={loading}
              ghost={
                <GhostCardContainer>
                  <GhostDataTable
                    headers={headers}
                    rows={4}
                    title={true}
                    description={false}
                    showSearch={false}
                  />
                </GhostCardContainer>
              }
            >
              <DataTable
                title="Payout History"
                headers={headers}
                rows={filteredPayouts}
                filterElement={true}
                onFilterClick={() => {
                  setDraftFilters(filters);
                  setIsFilterOpen(true);
                }}
                currentPage={currentPage}
                totalPages={totalPagesCalculation}
                onPageChange={(page) => setCurrentPage(page)}
                emptyStateTitle="No payouts yet"
                emptyStateText="Your payout history will appear here once you start receiving payouts from our platform"
              />
            </LoadingToRealTransitionWrapper>
          </Fragment>
        )}
      </div>

      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={() => {
          setFilters(draftFilters);
          setIsFilterOpen(false);
        }}
        title={filterContent.payouts.title}
        applyText={filterContent.apply}
      >
        <div className="filter-field">
          <input
            className="form-input"
            type="date"
            value={draftFilters.date}
            onChange={(e) =>
              setDraftFilters((prev) => ({ ...prev, date: e.target.value }))
            }
          />
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.status}
            onChange={(e) =>
              setDraftFilters((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <option value="">{filterContent.all_statuses}</option>
            <option value="Completed">{filterContent.status.completed}</option>
            <option value="Active">{filterContent.status.active}</option>
            <option value="Cancelled">{filterContent.status.cancelled}</option>
          </select>
        </div>
      </FilterModal>
    </Fragment>
  );
};

export default Payouts;
